import ComplementaryProducts from './ComplementaryProducts';
import Modal from '../Modal';
import ProductForm from './ProductForm';
import ProductImages from './ProductImages';
import { formatMoney } from '@shopify/theme-currency';

const selectors = {
  priceWrapper: '[data-price-wrapper]',
  productPrice: '[data-product-price]',
  comparePrice: '[data-compare-price]',
  comparePriceText: '[data-compare-text]',
  dimensionsContent: '[data-pdp-dimensions-content]',
  dimensionsOpenButton: '[data-open-dimensions]',
};

const cssClasses = {
  hidden: 'hidden',
};

/**
 * The wrapper class for PDP components
 *
 * @export
 * @class Product
 */
export default class Product {
  /**
   * Creates an instance of Product.
   *
   * @param {HTMLElement} el The container of this product
   * @param {Object} config Settings object
   * @param {String} config Section ID
   */
  constructor(el, config = {}, sectionId) {
    this.el = el;
    this.sectionId = sectionId;
    this.config = Object.assign(
      { isQuickShop: false, isFeaturedProduct: false },
      config,
    );
    this.modal = new Modal();

    this._initComponents();

    const dimensionsOpenButton = el.querySelector(
      selectors.dimensionsOpenButton,
    );
    if (dimensionsOpenButton) {
      dimensionsOpenButton.addEventListener(
        'click',
        this._openDimensionsModal(el),
      );
    }
  }

  /**
   * Opens drawer.
   *
   * @param {object} container - Container of form to be opened in the drawer.
   */
  _openDimensionsModal = container => event => {
    const modal = container.querySelector(selectors.dimensionsContent);
    const content = modal.innerHTML;
    this.modal.open(content);
  };

  /**
   * Init all components that make up the product
   */
  _initComponents() {
    this.productImages = new ProductImages(this.el);
    this.productForm = new ProductForm(this.el, {
      onVariantChange: variant => this._onVariantChange(variant),
      isQuickShop: this.config.isQuickShop,
      isFeaturedProduct: this.config.isFeaturedProduct,
    });
    new ComplementaryProducts(this.el, this.sectionId);
  }

  /**
   * Call functions to update on variant change from product form
   *
   * @param {Object} variant
   */
  _onVariantChange = variant => {
    this.productImages.change(variant);
    this._updatePrice(variant);
    this._updateSelectedOptionTitle(variant);
  };

  /**
   * Update prices when selected variant changes
   *
   * @param {Object} variant
   */
  _updatePrice = variant => {
    const comparePrice = this.el.querySelectorAll(selectors.comparePrice);
    const compareText = this.el.querySelectorAll(selectors.comparePriceText);
    const mainPrice = this.el.querySelectorAll(selectors.productPrice);

    const isOnSale = variant.compare_at_price > variant.price;
    const comparePriceText = isOnSale
      ? formatMoney(variant.compare_at_price, theme.moneyFormat)
      : '';

    // Update the product price
    mainPrice.forEach(price => {
      price.innerHTML = formatMoney(variant.price, theme.moneyFormat);
    });

    // Update compare at price depending on whether variant is on sale
    comparePrice.forEach((price, index) => {
      price.innerHTML = comparePriceText;
      if (isOnSale) {
        price.classList.remove(cssClasses.hidden);
        compareText[index].classList.remove(cssClasses.hidden);
      } else {
        price.classList.add(cssClasses.hidden);
        compareText[index].classList.add(cssClasses.hidden);
      }
    });
  };

  _updateSelectedOptionTitle = variant => {
    const option1Span = document.querySelector(
      '[data-current-option="option1"]',
    );
    const option2Span = document.querySelector(
      '[data-current-option="option2"]',
    );
    const option3Span = document.querySelector(
      '[data-current-option="option3"]',
    );

    if (option1Span) {
      option1Span.innerHTML = variant.option1;
    }
    if (option2Span) {
      option2Span.innerHTML = variant.option2;
    }
    if (option3Span) {
      option3Span.innerHTML = variant.option3;
    }
  };
}
